import { PaymentHandoffInstruction, PaymentProcessor } from '@common/entities';
import { getPaymentIntegration } from '@common/validation-schemas';
import { PaymentStore } from 'state/slices/paymentSlice';

import { buildTransferMatePaymentRequest } from './transfermate/utils';
import { PAYMENT_METHOD, PAYMENT_TYPE_ID, STRIPE_PAYMENT } from './constants';
import { startStripeCheckout } from './startStripeCheckout';
import { startTransferMatePayment } from './startTransferMatePayment';

interface PrepareContext {
  paymentState: PaymentStore;
  amount: number;
  paymentTypeId: number;
  applicantId?: number;
  applicationId?: number;
  translate: (key: string) => string;
}
export interface PaymentExecutionContext {
  websiteHost: string;
  translate: (key: string) => string;
  onProcessing: () => void;
  onPaymentId: (id: string) => void;
}
type PaymentOutcome = 'redirected' | 'completed';
type RequestFor<P extends PaymentProcessor> = Extract<
  PaymentHandoffInstruction,
  { processor: P }
>['payment'];
type Adapter<P extends PaymentProcessor> = {
  prepare: (context: PrepareContext) => RequestFor<P>;
  start: (payment: RequestFor<P>, context: PaymentExecutionContext) => Promise<PaymentOutcome>;
};

/** Each vendor is implemented once, shared by standalone and authenticated continuation. */
const adapters: { [P in PaymentProcessor]: Adapter<P> } = {
  stripe: {
    prepare: ({ amount, paymentTypeId, applicantId, applicationId }) => ({
      applicantId,
      applicationId,
      paymentAmount: amount,
      paymentTypeId,
      paymentMemo: STRIPE_PAYMENT.MEMO,
      referenceType: STRIPE_PAYMENT.REFERENCE_TYPE,
    }),
    start: async (payment, context) => {
      await startStripeCheckout(
        { ...payment, websiteHost: context.websiteHost.split(/[?#]/)[0] },
        context.translate('payment.international.invalidStripeResponse'),
        () => undefined,
      );
      return 'redirected';
    },
  },
  transfermate: {
    prepare: ({ paymentState, amount, applicantId, applicationId, translate }) => {
      const {
        internationalPaymentFormValid,
        internationalTransferMateFields,
        internationalTransferMateRate,
        termsAndConditionAck,
      } = paymentState;
      if (internationalPaymentFormValid !== true)
        throw new Error(translate('payment.international.transferMateFieldsRequired'));
      if (!internationalTransferMateFields || !internationalTransferMateRate)
        throw new Error(translate('payment.international.transferMateNotAvailable'));
      const { websiteHost, ...payment } = buildTransferMatePaymentRequest(
        internationalTransferMateFields,
        internationalTransferMateRate,
        amount,
        '',
        termsAndConditionAck,
      );
      return { ...payment, applicantId, applicationId };
    },
    start: (payment, context) =>
      startTransferMatePayment(
        { ...payment, websiteHost: context.websiteHost },
        context.translate('payment.international.invalidTransferMateResponse'),
        context.onPaymentId,
      ),
  },
};

const methodIds: Record<string, number> = {
  [PAYMENT_METHOD.CARD]: PAYMENT_TYPE_ID.CARD,
  [PAYMENT_METHOD.BACS_DIRECT_DEBIT]: PAYMENT_TYPE_ID.BACS_DIRECT_DEBIT,
  [PAYMENT_METHOD.PAY_BY_BANK]: PAYMENT_TYPE_ID.PAY_BY_BANK,
};

export function preparePayment(
  context: Omit<PrepareContext, 'paymentTypeId'>,
): PaymentHandoffInstruction {
  const { paymentState, translate } = context;
  if (!paymentState.termsAndConditionAck)
    throw new Error(translate('payment.international.termsRequired'));
  const paymentTypeId = methodIds[paymentState.selectedPaymentType ?? ''];
  const selected = paymentState.paymentTypes.find((type) => type.id === paymentTypeId);
  const processor = selected?.processor;
  getPaymentIntegration(processor); // Missing/unknown integrations must never become Stripe payments.
  if (context.amount <= 0) throw new Error(translate('payment.international.invalidAmount'));
  const payment = adapters[processor!].prepare({ ...context, paymentTypeId });
  return { processor, payment } as PaymentHandoffInstruction;
}

export function requiresPaymentHandoff(processor: PaymentProcessor, embedded: boolean): boolean {
  return embedded && getPaymentIntegration(processor).requiresTopLevel;
}

export async function executePayment(
  instruction: PaymentHandoffInstruction,
  context: PaymentExecutionContext,
): Promise<PaymentOutcome> {
  getPaymentIntegration(instruction.processor);
  context.onProcessing();
  // The discriminated instruction binds its request to the matching adapter.
  const adapter = adapters[instruction.processor] as Adapter<PaymentProcessor>;
  return adapter.start(instruction.payment, context);
}

/** Compatibility for handoffs issued before the generic instruction contract. */
export function readPaymentInstruction(data: {
  instruction?: PaymentHandoffInstruction;
  payment?: RequestFor<'stripe'>;
}): PaymentHandoffInstruction | undefined {
  return (
    data.instruction ?? (data.payment ? { processor: 'stripe', payment: data.payment } : undefined)
  );
}
